import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, CalendarHeart, Droplet, LogOut, Globe, Instagram, Star, BarChart3, Landmark, Building2, MessagesSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, LINKS } from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/pazienti", label: "Pazienti", icon: Users, id: "patients" },
  { to: "/messaggi", label: "Messaggi", icon: MessagesSquare, id: "messages" },
  { to: "/studi", label: "Studi", icon: Building2, id: "studios" },
  { to: "/appuntamenti", label: "Appuntamenti", icon: CalendarDays, id: "appointments" },
  { to: "/agenda", label: "Agenda personale", icon: CalendarHeart, id: "agenda" },
  { to: "/prelievi", label: "Prelievi", icon: Droplet, id: "blood-draws" },
  { to: "/report", label: "Report mensile", icon: BarChart3, id: "report" },
  { to: "/amministrazione", label: "Amministrazione", icon: Landmark, id: "administration" },
];

export const Brand = ({ light = false }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo">
    <img src="/img/logo-mark.png" alt="Logo Dott.ssa Simona Marretta" className="h-10 w-auto" />
    <div className="leading-tight">
      <div className={`text-sm font-bold ${light ? "text-white" : "text-[#0f172a]"}`}>Dott.ssa Simona Marretta</div>
      <div className={`text-[11px] ${light ? "text-white/70" : "text-[#b8956f]"}`}>Biologa Nutrizionista</div>
    </div>
  </div>
);

const navCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
    isActive ? "bg-[#3d655b] text-white shadow-sm" : "text-slate-600 hover:bg-[#edf2ef] hover:text-[#2f4f47]"
  }`;

const ExtLinks = () => (
  <div className="space-y-1">
    <div className="label-xs px-4 pb-1">Link utili</div>
    {[
      { href: LINKS.site, label: "Sito web", icon: Globe, id: "site" },
      { href: LINKS.instagram, label: "Instagram", icon: Instagram, id: "instagram" },
      { href: LINKS.review, label: "Recensioni Google", icon: Star, id: "review" },
    ].map((l) => (
      <a key={l.id} href={l.href} target="_blank" rel="noreferrer" data-testid={`sidebar-link-${l.id}`}
        className="flex items-center gap-3 rounded-full px-4 py-2 text-xs font-medium text-slate-500 hover:bg-slate-100">
        <l.icon className="h-4 w-4" /> {l.label}
      </a>
    ))}
  </div>
);

const useUnread = () => {
  const [n, setN] = useState(0);
  const { pathname, search } = useLocation();
  useEffect(() => {
    const load = () => api.get("/messages/unread-count").then((r) => setN(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [pathname, search]);
  return n;
};

const Unread = ({ n, testid }) => (n > 0 ? <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-[#b8956f] px-1 text-[10px] font-bold text-white" data-testid={testid}>{n}</span> : null);

export function AppShell() {
  const { user, logout } = useAuth();
  const unread = useUnread();
  return (
    <div className="min-h-screen lg:flex">
      <aside className="no-print hidden w-72 shrink-0 flex-col justify-between border-r border-[#e2e8f0] bg-white p-6 lg:flex lg:sticky lg:top-0 lg:h-screen">
        <div className="space-y-8">
          <Brand />
          <nav className="space-y-1" data-testid="sidebar-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={navCls} data-testid={`nav-${n.id}`}>
                <n.icon className="h-4 w-4" /> {n.label}{n.id === "messages" && <Unread n={unread} testid="nav-messages-unread" />}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="space-y-5">
          <ExtLinks />
          <div className="rounded-2xl bg-[#edf2ef] p-4">
            <div className="truncate text-xs text-[#2f4f47]" data-testid="sidebar-user-email">{user?.email}</div>
            <button onClick={logout} className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#0f172a]" data-testid="logout-button">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </aside>
      <header className="no-print sticky top-0 z-30 border-b border-[#e2e8f0] bg-white/90 backdrop-blur-xl lg:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <Brand />
          <button onClick={logout} className="btn-ghost" data-testid="logout-button-mobile"><LogOut className="h-4 w-4" /></button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={navCls} data-testid={`nav-mobile-${n.id}`}>
              <n.icon className="h-4 w-4" /> {n.label}{n.id === "messages" && <Unread n={unread} testid="nav-mobile-messages-unread" />}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="min-w-0 flex-1 px-4 py-6 sm:px-8 lg:py-10">
        <div className="mx-auto max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
